class SurveyComponent extends HTMLElement {
  constructor() {
    super();
    
    this.currentStepIndex = 0;
    this.collectedData = {};
    this.steps = [];
  }

  connectedCallback() {
    // Get static attributes (these don't change dynamically)
    this.fromUrl = this.getAttribute('fromUrl') || '';
    this.toAction = this.getAttribute('to') || '';
    this.progressSegments = parseInt(this.getAttribute('progress-segments')) || 6;
    
    // By default, hide all surveys
    this.style.display = 'none';
    
    // Check URL parameter to show the correct survey
    const urlParams = new URLSearchParams(window.location.search);
    const showSurvey = urlParams.get('showSurvey');
    const surveyId = this.id;
    
    // Only show this survey if the URL parameter matches its ID
    if (showSurvey === surveyId) {
      this.style.display = 'block';
    }
    
    // Create progress bar container
    this.createProgressBar();
    
    // Initialize survey
    this.init();
  }

  createProgressBar() {
    // Create progress bar element
    this.progressBar = document.createElement('div');
    this.progressBar.className = 'survey-progress-bar';
    this.progressBar.innerHTML = `
      <div class="survey-progress-wrapper">
        <div class="survey-progress-container">
          ${Array.from({ length: this.progressSegments }, (_, i) => 
            `<div class="survey-progress-segment" data-segment="${i + 1}"></div>`
          ).join('')}
        </div>
        <p class="survey-progress-text" style="display: none;"></p>
      </div>
    `;
    
    // Insert at the beginning of survey component
    this.insertBefore(this.progressBar, this.firstChild);
    this.progressBar.style.display = 'none'; // Hidden by default
    
    // Add a class to prevent theme.css from hiding it when "empty"
    this.progressBar.classList.add('no-empty');
  }

  init() {
    // Find all step-survey-component children
    this.steps = Array.from(this.querySelectorAll('step-survey-component'));
    
    if (this.steps.length === 0) {
      console.warn('No steps found in survey-component');
      return;
    }

    // Initialize first step
    this.showStep(0);
    
    // Listen for step navigation events
    this.addEventListener('survey:next', this.handleNext.bind(this));
    this.addEventListener('survey:previous', this.handlePrevious.bind(this));
    this.addEventListener('survey:submit', this.handleSubmit.bind(this));
    this.addEventListener('survey:finish', this.handleFinish.bind(this));
    this.addEventListener('survey:collectData', this.handleCollectData.bind(this));
    
    console.log('📋 Survey initialized with', this.steps.length, 'steps');
  }

  showStep(index) {
    // Hide all steps
    this.steps.forEach((step, i) => {
      if (i === index) {
        step.style.display = 'block';
        step.setAttribute('active', '');
        
        // Update progress bar
        this.updateProgressBar(step);
        
        // If showing cervical screening exit page (Step 6A), populate reasons
        if (step.getAttribute('step') === '6A' && this.id === 'cervical-screening-survey') {
          // Use setTimeout to ensure DOM is ready
          setTimeout(() => {
            const stepElement = this.steps[index];
            if (stepElement && typeof stepElement.populateCervicalExitReasons === 'function') {
              stepElement.populateCervicalExitReasons();
            }
          }, 100);
        }
      } else {
        step.style.display = 'none';
        step.removeAttribute('active');
      }
    });
    
    this.currentStepIndex = index;
    console.log('📍 Showing step:', index);
  }

  updateProgressBar(step) {
    const showProgress = step.getAttribute('show-progress') === 'true';
    const segment = parseInt(step.getAttribute('progress-segment')) || 0;
    const percentage = parseInt(step.getAttribute('progress-percentage')) || 0;
    const progressText = step.getAttribute('progress-text') || '';
    
    console.log('📊 updateProgressBar called');
    console.log('📊 show-progress attribute:', step.getAttribute('show-progress'));
    console.log('📊 showProgress (boolean):', showProgress);
    console.log('📊 segment:', segment);
    console.log('📊 percentage:', percentage);
    console.log('📊 progressText:', progressText);
    console.log('📊 progressBar element:', this.progressBar);
    
    if (!showProgress) {
      this.progressBar.style.display = 'none';
      console.log('📊 Progress bar hidden');
      return;
    }
    
    this.progressBar.style.setProperty('display', 'block', 'important');
    console.log('📊 Progress bar shown');
    console.log('📊 progressBar display style after setting block:', this.progressBar.style.display);
    
    // Update progress text
    const textElement = this.progressBar.querySelector('.survey-progress-text');
    if (textElement) {
      if (progressText) {
        textElement.textContent = progressText;
        textElement.style.display = 'block';
        console.log('📊 Progress text shown:', progressText);
      } else {
        textElement.style.display = 'none';
        console.log('📊 Progress text hidden');
      }
    }
    
    // Reset all segments
    const segments = this.progressBar.querySelectorAll('.survey-progress-segment');
    segments.forEach((seg, idx) => {
      const segmentNum = idx + 1;
      
      if (segmentNum < segment) {
        // Completed segments - full red
        seg.style.backgroundColor = '#c22026';
        seg.classList.add('completed');
        seg.classList.remove('active');
      } else if (segmentNum === segment) {
        // Current segment - always fill completely (red)
        seg.style.backgroundColor = '#c22026';
        seg.classList.add('active');
        seg.classList.remove('completed');
      } else {
        // Future segments - pink
        seg.style.backgroundColor = '#ecd7e9';
        seg.classList.remove('completed', 'active');
      }
    });
    
    console.log('📊 Progress updated: Segment', segment, '/', this.progressSegments, '-', percentage + '%');
  }

  handleNext(event) {
    const { toNext } = event.detail;
    
    if (toNext) {
      // Handle conditional step (named step)
      const targetStep = this.steps.findIndex(step => step.getAttribute('step') === toNext);
      if (targetStep !== -1) {
        this.showStep(targetStep);
      } else {
        console.error('Target step not found:', toNext);
      }
    } else if (this.currentStepIndex < this.steps.length - 1) {
      // Go to next sequential step
      this.showStep(this.currentStepIndex + 1);
    } else {
      console.log('Already at last step');
    }
  }

  handlePrevious(event) {
    const currentStep = this.steps[this.currentStepIndex];
    const fromPrevious = currentStep.getAttribute('fromPrevious');
    
    if (fromPrevious) {
      // Handle conditional previous step
      const targetStep = this.steps.findIndex(step => step.getAttribute('step') === fromPrevious);
      if (targetStep !== -1) {
        this.showStep(targetStep);
      }
    } else if (this.currentStepIndex > 0) {
      // Go to previous sequential step
      this.showStep(this.currentStepIndex - 1);
    }
  }

  handleCollectData(event) {
    const { tableName, formData } = event.detail;
    
    if (tableName && formData) {
      this.collectedData[tableName] = formData;
      console.log('📊 Data collected for:', tableName, formData);
    }
  }

  handleSubmit(event) {
    console.log('✅ Step submitted');
    // Move to next step after form submission
    this.handleNext(event);
  }

  handleFinish(event) {
    console.log('🎉 Survey finished!');
    console.log('📦 All collected data:', this.collectedData);
    
    // Add product to cart and go to checkout directly
    this.addToCartAndCheckout();
  }

  performFinalAction() {
    const productId = this.getAttribute('product') || '';
    const quantity = this.getAttribute('quantity') || '1';
    
    switch (this.toAction) {
      case 'cart':
        console.log('Adding to cart:', productId, 'quantity:', quantity);
        this.addToCartAndCheckout();
        break;
      case 'redirect':
        console.log('Redirecting...');
        // Redirect logic here
        break;
      default:
        console.log('No action specified');
    }
  }

  showCalendlyPopup() {
    const modal = document.querySelector('#calendly-overlay');
    if (!modal) {
      console.error('SurveyComponent: Calendly modal not found in DOM. Make sure to include the calendly-popup snippet.');
      // If no Calendly modal, proceed directly to final action
      this.performFinalAction();
      return;
    }
    
    const closeButton = modal.querySelector('.calendly-modal-close');
    
    // Load Calendly script if not already loaded
    if (!document.querySelector('script[src*="calendly.com"]')) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.head.appendChild(script);
    }
    
    // Set up Calendly event listeners for when an event is scheduled
    const handleCalendlyEvent = (e) => {
      if (e.data.event && (e.data.event === 'calendly.event_scheduled' || e.data.event.includes('scheduled'))) {
        console.log('Calendly event scheduled, adding to cart and proceeding to checkout...');
        
        if (window.closeCalendlyModalAnimated) {
          window.closeCalendlyModalAnimated();
        }
        
        setTimeout(() => {
          this.performFinalAction();
        }, 500);
      }
    };
    
    // Listen for Calendly events
    window.addEventListener('message', handleCalendlyEvent);
    
    window.closeCalendlyModalAnimated = () => {
      modal.classList.remove('active');
      
      setTimeout(() => {
        modal.style.display = 'none';
        delete window.closeCalendlyModalAnimated;
        document.body.classList.remove('tw-overflow-hidden');
        window.removeEventListener('message', handleCalendlyEvent);
      }, 300);
    };
    
    modal.addEventListener('click', (e) => {
      const calendlyWidget = modal.querySelector('.calendly-inline-widget');
      if (calendlyWidget && !calendlyWidget.contains(e.target)) {
        window.closeCalendlyModalAnimated();
      }
    });
    
    if (closeButton) {
      closeButton.onclick = window.closeCalendlyModalAnimated;
    }
    
    document.body.classList.add('tw-overflow-hidden');
    modal.style.display = 'block';
    
    requestAnimationFrame(() => {
      modal.classList.add('active');
    });
  }

  addToCartAndCheckout() {
    // Read attributes dynamically (they may have been set by JavaScript after connectedCallback)
    const productId = this.getAttribute('product') || '';
    const quantity = this.getAttribute('quantity') || '1';
    
    console.log('addToCartAndCheckout - productId:', productId, 'quantity:', quantity);
    
    if (!productId) {
      console.error('SurveyComponent: No product ID specified');
      return;
    }

    // Collect all form data from the survey
    const surveyData = this.collectAllFormData();
    console.log('📋 Survey data collected:', surveyData);

    // Build the cart item with properties
    const cartItem = {
      id: productId,
      quantity: parseInt(quantity),
      properties: surveyData
    };

    fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(cartItem)
    })
      .then(response => response.json())
      .then(data => {
        console.log('Product added to cart with properties:', data);
        window.location.href = '/checkout';
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        // Still try to go to checkout
        window.location.href = '/checkout';
      });
  }

  collectAllFormData() {
    const formData = {};
    
    // Get all steps (including hidden ones)
    const allSteps = this.querySelectorAll('step-survey-component');
    console.log('📋 Found steps:', allSteps.length);
    
    // Collect all inputs from ALL steps (including hidden ones)
    // Use a broader selector to catch all input types
    const allInputs = this.querySelectorAll('input');
    console.log('📋 Found all inputs:', allInputs.length);
    console.log('📋 All inputs:', allInputs);
    
    allInputs.forEach(input => {
      const inputType = input.type;
      const inputName = input.name;
      const inputValue = input.value;
      
      // Skip inputs without name or search inputs
      if (!inputName || input.classList.contains('country-search')) {
        return;
      }
      
      // Handle different input types
      if (inputType === 'text' || inputType === 'email' || inputType === 'tel' || inputType === 'date' || inputType === 'number') {
        if (inputValue) {
          formData[inputName] = inputValue;
          console.log('📋 Collected text:', inputName, '=', inputValue);
        }
      } else if (inputType === 'radio') {
        if (input.checked) {
          formData[inputName] = inputValue;
          console.log('📋 Collected radio:', inputName, '=', inputValue);
        }
      } else if (inputType === 'checkbox') {
        if (input.checked) {
          if (!formData[inputName]) {
            formData[inputName] = [];
          }
          if (Array.isArray(formData[inputName])) {
            formData[inputName].push(inputValue);
          }
          console.log('📋 Collected checkbox:', inputName, '=', inputValue);
        }
      } else if (inputType === 'file') {
        if (input.files && input.files.length > 0) {
          formData[inputName] = input.files[0].name;
          console.log('📋 Collected file:', inputName, '=', input.files[0].name);
        }
      }
    });
    
    // Convert checkbox arrays to comma-separated strings
    Object.keys(formData).forEach(key => {
      if (Array.isArray(formData[key])) {
        formData[key] = formData[key].join(', ');
      }
    });
    
    // Collect all textareas
    const textareas = this.querySelectorAll('textarea');
    textareas.forEach(textarea => {
      if (textarea.value && textarea.name) {
        formData[textarea.name] = textarea.value;
        console.log('📋 Collected textarea:', textarea.name, '=', textarea.value);
      }
    });
    
    // Collect select dropdowns
    const selects = this.querySelectorAll('select');
    selects.forEach(select => {
      if (select.value && select.name) {
        formData[select.name] = select.value;
        console.log('📋 Collected select:', select.name, '=', select.value);
      }
    });
    
    return formData;
  }
}

customElements.define('survey-component', SurveyComponent);
